import { useCallback, useEffect, useRef, useState } from "react";

/* =========================================================
 * Tipos públicos
 * ========================================================= */

export type CoordenadorAPI = {
  id: number | null;
  nome: string;
  email?: string | null;
  telefone?: string | null;
};

export type ClienteAPI = {
  id: number | null;
  empresa_cliente_id: number | null;
  empresa_id?: number | null;

  cnpj: string;
  razao_social: string;
  nome_fantasia: string;

  email?: string | null;
  telefone?: string | null;
  ativo?: boolean | null;

  colaborador_interno_responsavel: number[];
  responsaveis: CoordenadorAPI[];
  responsaveis_nomes: string[];
};

export type AtualizarEmpresaClientePayload = {
  empresa_cliente_id: number;
  adicionar_coordenadores_ids?: number[];
  remover_coordenadores_ids?: number[];
  colaborador_interno_responsavel_ids?: number[];
};

export type AtualizarEmpresaClienteResponse = {
  ok?: boolean;
  erro?: string;
  erros?: string[];
  trace_id?: string;
  data?: unknown;

  alteracoes_coordenadores?: {
    anteriores?: number[];
    atuais?: number[];
    adicionados?: number[];
    removidos?: number[];
  };
};

type RespAPI = {
  ok?: boolean;
  meta?: unknown;
  data?: unknown[];
  erro?: string;
  erros?: string[];
  mensagem?: string;
  message?: string;
};

type ApiEnvelope<T = unknown> = {
  ok?: boolean;
  data?: T;
  meta?: unknown;
  erro?: string;
  erros?: string[];
  mensagem?: string;
  message?: string;
  [key: string]: unknown;
};

/* =========================================================
 * Endpoints
 * ========================================================= */

const API_BASE = "https://api.eva-googlemarine.com";

const URL_CLIENTES = `${API_BASE}/geral/empresa-clientes-completo`;

const URL_COORDENADORES = `${API_BASE}/departamentos/recursos-humanos/colaboradores-coordenacao`;

const ACCESS_TOKEN_KEYS = [
  "accessToken",
  "eva:accessToken",
  "access_token",
] as const;

const POLL_INTERVAL_MS = 15_000;

function buildAtualizarEmpresaUrl(apiBase: string): string {
  const base = String(apiBase ?? "")
    .trim()
    .replace(/\/+$/, "");

  if (!base) {
    throw new Error("Base da API não informada.");
  }

  return `${base}/departamentos/coordenacao/empresa-clientes/atualizar`;
}

/* =========================================================
 * Helpers básicos
 * ========================================================= */

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

function onlyDigits(value: unknown): string {
  return String(value ?? "").replace(/\D+/g, "");
}

function normalizeText(value: unknown): string {
  return String(value ?? "").trim();
}

function normalizeName(value: unknown): string {
  return normalizeText(value).toLocaleUpperCase("pt-BR").replace(/\s+/g, " ");
}

function normalizeId(value: unknown): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function normalizeIdArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return [
    ...new Set(
      value.map(normalizeId).filter((id): id is number => id !== null)
    ),
  ];
}

export function formatCNPJ(cnpj: string): string {
  const digits = onlyDigits(cnpj);

  if (!digits) {
    return "—";
  }

  const normalized = digits.padStart(14, "0").slice(-14);

  return normalized.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    "$1.$2.$3/$4-$5"
  );
}

/* =========================================================
 * Autenticação e HTTP
 * ========================================================= */

function getStoredAccessToken(): string {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    for (const key of ACCESS_TOKEN_KEYS) {
      const value = window.localStorage.getItem(key)?.trim();

      if (value) {
        return value;
      }
    }
  } catch {
    // localStorage indisponível.
  }

  return "";
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getStoredAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function readJsonSafe(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function getApiErrorMessage(response: Response, payload: unknown): string {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const envelope = payload as ApiEnvelope;

    if (typeof envelope.erro === "string" && envelope.erro.trim()) {
      return envelope.erro.trim();
    }

    if (Array.isArray(envelope.erros) && envelope.erros.length > 0) {
      return envelope.erros
        .map((item) => String(item ?? "").trim())
        .filter(Boolean)
        .join(" | ");
    }

    if (typeof envelope.mensagem === "string" && envelope.mensagem.trim()) {
      return envelope.mensagem.trim();
    }

    if (typeof envelope.message === "string" && envelope.message.trim()) {
      return envelope.message.trim();
    }
  }

  return `HTTP ${response.status}`;
}

async function requestJSON<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    cache: "no-store",
    credentials: "include",
    ...init,
  });

  const payload = await readJsonSafe(response);

  const envelope =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as ApiEnvelope<T>)
      : null;

  if (!response.ok || envelope?.ok === false) {
    console.error("Erro completo da API:", {
      url,
      status: response.status,
      statusText: response.statusText,
      payload,
    });

    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      throw new Error(JSON.stringify(payload, null, 2));
    }

    throw new Error(getApiErrorMessage(response, payload));
  }

  if (envelope && Object.prototype.hasOwnProperty.call(envelope, "data")) {
    return envelope.data as T;
  }

  return payload as T;
}

/* =========================================================
 * Normalização dos coordenadores
 * ========================================================= */

function normalizeCoordenador(value: unknown): CoordenadorAPI | null {
  if (typeof value === "string") {
    const nome = normalizeText(value);

    if (!nome) {
      return null;
    }

    return {
      id: null,
      nome,
      email: null,
      telefone: null,
    };
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const item = value as Record<string, unknown>;

  const id = normalizeId(
    item.id ??
      item.coordenador_id ??
      item.responsavel_id ??
      item.usuario_id ??
      item.user_id ??
      item.pessoa_id
  );

  const nome = normalizeText(
    item.nome ??
      item.name ??
      item.coordenador_nome ??
      item.responsavel_nome ??
      item.usuario_nome ??
      item.pessoa_nome ??
      item.nome_completo
  );

  if (!nome && id === null) {
    return null;
  }

  const email =
    normalizeText(item.email ?? item.mail ?? item.usuario_email) || null;

  const telefone =
    normalizeText(item.telefone ?? item.phone ?? item.celular) || null;

  return {
    id,

    nome: nome || `Coordenador ${id}`,

    email,
    telefone,
  };
}

function deduplicateCoordenadores(
  coordenadores: CoordenadorAPI[]
): CoordenadorAPI[] {
  const result: CoordenadorAPI[] = [];

  for (const coordenador of coordenadores) {
    const id = normalizeId(coordenador.id);

    const nome = normalizeText(coordenador.nome);

    if (!nome && id === null) {
      continue;
    }

    const nomeNormalizado = normalizeName(nome);

    const existingIndex = result.findIndex((item) => {
      const existingId = normalizeId(item.id);

      if (id !== null && existingId === id) {
        return true;
      }

      return Boolean(
        nomeNormalizado && normalizeName(item.nome) === nomeNormalizado
      );
    });

    if (existingIndex === -1) {
      result.push({
        ...coordenador,
        id,

        nome: nome || `Coordenador ${id}`,
      });

      continue;
    }

    const existing = result[existingIndex];

    result[existingIndex] = {
      id: existing.id ?? id,

      nome:
        existing.nome && !/^Coordenador \d+$/.test(existing.nome)
          ? existing.nome
          : nome || existing.nome,

      email: existing.email ?? coordenador.email ?? null,

      telefone: existing.telefone ?? coordenador.telefone ?? null,
    };
  }

  return result.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

function extractCoordenadoresFromCliente(
  item: Record<string, unknown>
): CoordenadorAPI[] {
  const result: CoordenadorAPI[] = [];

  const responsaveisIds = normalizeIdArray(
    item.colaborador_interno_responsavel ??
      item.colaborador_interno_responsavel_ids ??
      item.responsaveis_ids ??
      item.coordenadores_ids
  );

  const arraysCompletos = [
    item.responsaveis,
    item.coordenadores,
    item.responsaveis_detalhes,
    item.coordenadores_detalhes,
    item.coordenadores_responsaveis,
    item.responsaveis_completos,
  ];

  for (const possibleArray of arraysCompletos) {
    if (!Array.isArray(possibleArray)) {
      continue;
    }

    possibleArray.forEach((value, index) => {
      const coordenador = normalizeCoordenador(value);

      if (!coordenador) {
        return;
      }

      result.push({
        ...coordenador,

        id: coordenador.id ?? responsaveisIds[index] ?? null,
      });
    });
  }

  const arraysNomes = [
    item.responsaveis_nomes,
    item.responsaveisNomes,
    item.coordenadores_nomes,
    item.coordenadoresNomes,
  ];

  for (const possibleArray of arraysNomes) {
    if (!Array.isArray(possibleArray)) {
      continue;
    }

    possibleArray.forEach((value, index) => {
      const coordenador = normalizeCoordenador(value);

      if (!coordenador) {
        return;
      }

      result.push({
        ...coordenador,

        id: coordenador.id ?? responsaveisIds[index] ?? null,
      });
    });
  }

  for (const id of responsaveisIds) {
    const alreadyExists = result.some((item) => normalizeId(item.id) === id);

    if (!alreadyExists) {
      result.push({
        id,

        nome: `Coordenador ${id}`,

        email: null,
        telefone: null,
      });
    }
  }

  return deduplicateCoordenadores(result);
}

/* =========================================================
 * Normalização das empresas
 * ========================================================= */

function normalizeClientes(values: unknown[]): ClienteAPI[] {
  return (values || [])
    .filter((value): value is Record<string, unknown> =>
      Boolean(value && typeof value === "object" && !Array.isArray(value))
    )
    .map((item) => {
      const empresaClienteId = normalizeId(
        item.id ??
          item.empresa_cliente_id ??
          item.empresaClienteId ??
          item.cliente_id
      );

      const responsaveisIds = normalizeIdArray(
        item.colaborador_interno_responsavel ??
          item.colaborador_interno_responsavel_ids ??
          item.responsaveis_ids ??
          item.coordenadores_ids
      );

      const responsaveis = extractCoordenadoresFromCliente(item);

      return {
        id: empresaClienteId,

        empresa_cliente_id: empresaClienteId,

        empresa_id: normalizeId(item.empresa_id ?? item.empresaId),

        cnpj: normalizeText(
          item.cnpj ?? item.CNPJ ?? item.documento ?? item.cnpj_cliente
        ),

        razao_social: normalizeText(
          item.razao_social ??
            item.razaoSocial ??
            item.razao ??
            item.razao_social_nome ??
            item.nome_empresarial
        ),

        nome_fantasia: normalizeText(
          item.nome_fantasia ?? item.nomeFantasia ?? item.fantasia ?? item.nome
        ),

        email:
          normalizeText(
            item.email ??
              item.email_principal ??
              item.mail ??
              item.email_empresa
          ) || null,

        telefone:
          normalizeText(
            item.telefone ??
              item.telefone_principal ??
              item.tel ??
              item.phone ??
              item.celular
          ) || null,

        ativo:
          typeof item.ativo === "boolean"
            ? item.ativo
            : item.ativo === "true"
            ? true
            : item.ativo === "false"
            ? false
            : null,

        colaborador_interno_responsavel: responsaveisIds,

        responsaveis,

        responsaveis_nomes: responsaveis.map((responsavel) => responsavel.nome),
      };
    });
}

/* =========================================================
 * Empresas
 * ========================================================= */

function extractClientesArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const envelope = payload as Record<string, unknown>;

  const directCandidates = [
    envelope.data,
    envelope.clientes,
    envelope.empresas,
    envelope.rows,
    envelope.resultados,
    envelope.items,
  ];

  for (const candidate of directCandidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  if (
    envelope.data &&
    typeof envelope.data === "object" &&
    !Array.isArray(envelope.data)
  ) {
    const nested = envelope.data as Record<string, unknown>;

    const nestedCandidates = [
      nested.data,
      nested.clientes,
      nested.empresas,
      nested.rows,
      nested.resultados,
      nested.items,
    ];

    for (const candidate of nestedCandidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }
  }

  return [];
}

async function requestClientes(authenticated: boolean): Promise<{
  response: Response;
  payload: unknown;
}> {
  const response = await fetch(URL_CLIENTES, {
    method: "GET",

    headers: authenticated
      ? buildAuthHeaders()
      : {
          Accept: "application/json",
        },

    ...(authenticated
      ? {
          credentials: "include" as RequestCredentials,
        }
      : {}),

    cache: "no-store",
  });

  const payload = await readJsonSafe(response);

  return {
    response,
    payload,
  };
}

export async function fetchClientesOnce(): Promise<{
  data: ClienteAPI[];
  meta: unknown | null;
}> {
  let result = await requestClientes(false);

  if (result.response.status === 401 || result.response.status === 403) {
    result = await requestClientes(true);
  }

  const { response, payload } = result;

  if (!response.ok) {
    throw new Error(getApiErrorMessage(response, payload));
  }

  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const envelope = payload as RespAPI;

    if (envelope.ok === false) {
      throw new Error(getApiErrorMessage(response, envelope));
    }
  }

  const values = extractClientesArray(payload);

  if (values.length === 0) {
    const possuiArrayVazioValido =
      Array.isArray(payload) ||
      (payload &&
        typeof payload === "object" &&
        (Array.isArray((payload as Record<string, unknown>).data) ||
          Array.isArray((payload as Record<string, unknown>).clientes) ||
          Array.isArray((payload as Record<string, unknown>).empresas) ||
          Array.isArray((payload as Record<string, unknown>).rows) ||
          Array.isArray((payload as Record<string, unknown>).resultados) ||
          Array.isArray((payload as Record<string, unknown>).items)));

    if (!possuiArrayVazioValido) {
      console.error(
        "Formato inesperado em empresa-clientes-completo:",
        payload
      );

      throw new Error(
        "A API respondeu, mas não retornou uma lista de empresas em data, clientes, empresas ou rows."
      );
    }
  }

  const meta =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as RespAPI).meta ?? null
      : null;

  return {
    data: normalizeClientes(values),

    meta,
  };
}

/* =========================================================
 * Coordenadores disponíveis
 * ========================================================= */

function extractArrayFromResponse(response: unknown): unknown[] {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const item = response as Record<string, unknown>;

  const possibilities = [
    item.data,
    item.coordenadores,
    item.responsaveis,
    item.usuarios,
    item.pessoas,
  ];

  for (const possibility of possibilities) {
    if (Array.isArray(possibility)) {
      return possibility;
    }
  }

  return [];
}

export async function fetchCoordenadores(): Promise<CoordenadorAPI[]> {
  const response = await requestJSON<unknown>(URL_COORDENADORES, {
    method: "GET",

    headers: buildAuthHeaders(),
  });

  const values = extractArrayFromResponse(response);

  return deduplicateCoordenadores(
    values
      .map(normalizeCoordenador)
      .filter(
        (coordenador): coordenador is CoordenadorAPI =>
          coordenador !== null && coordenador.id !== null
      )
  );
}

/* =========================================================
 * Atualização dos vínculos
 * ========================================================= */

export async function atualizarEmpresaCliente(
  apiBase: string,
  payload: AtualizarEmpresaClientePayload
): Promise<AtualizarEmpresaClienteResponse> {
  const empresaClienteId = normalizeId(payload.empresa_cliente_id);

  if (!empresaClienteId) {
    throw new Error("ID da empresa cliente inválido.");
  }

  const body: AtualizarEmpresaClientePayload = {
    empresa_cliente_id: empresaClienteId,
  };

  if (payload.adicionar_coordenadores_ids !== undefined) {
    body.adicionar_coordenadores_ids = normalizeIdArray(
      payload.adicionar_coordenadores_ids
    );
  }

  if (payload.remover_coordenadores_ids !== undefined) {
    body.remover_coordenadores_ids = normalizeIdArray(
      payload.remover_coordenadores_ids
    );
  }

  if (payload.colaborador_interno_responsavel_ids !== undefined) {
    body.colaborador_interno_responsavel_ids = normalizeIdArray(
      payload.colaborador_interno_responsavel_ids
    );
  }

  const url = buildAtualizarEmpresaUrl(apiBase);

  return requestJSON<AtualizarEmpresaClienteResponse>(url, {
    method: "POST",
    headers: buildAuthHeaders(true),
    credentials: "include",
    body: JSON.stringify(body),
  });
}

export async function adicionarCoordenadorEmpresa(
  apiBase: string,
  empresaClienteId: number,
  coordenadorId: number
): Promise<AtualizarEmpresaClienteResponse> {
  const clienteId = normalizeId(empresaClienteId);

  const responsavelId = normalizeId(coordenadorId);

  if (!clienteId) {
    throw new Error("ID da empresa cliente inválido.");
  }

  if (!responsavelId) {
    throw new Error("ID do coordenador inválido.");
  }

  return atualizarEmpresaCliente(apiBase, {
    empresa_cliente_id: clienteId,

    adicionar_coordenadores_ids: [responsavelId],
  });
}

export async function removerCoordenadorEmpresa(
  apiBase: string,
  empresaClienteId: number,
  coordenadorId: number
): Promise<AtualizarEmpresaClienteResponse> {
  const clienteId = normalizeId(empresaClienteId);

  const responsavelId = normalizeId(coordenadorId);

  if (!clienteId) {
    throw new Error("ID da empresa cliente inválido.");
  }

  if (!responsavelId) {
    throw new Error("ID do coordenador inválido.");
  }

  return atualizarEmpresaCliente(apiBase, {
    empresa_cliente_id: clienteId,

    remover_coordenadores_ids: [responsavelId],
  });
}

/* =========================================================
 * Hook principal
 * ========================================================= */

export function useEmpresasCliente() {
  const [data, setData] = useState<ClienteAPI[]>([]);

  const [loading, setLoading] = useState(true);

  const [meta, setMeta] = useState<unknown | null>(null);

  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  const fetchingRef = useRef(false);

  const carregar = useCallback(async () => {
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;

    try {
      const result = await fetchClientesOnce();

      if (!mountedRef.current) {
        return;
      }

      setData(result.data);

      setMeta(result.meta);

      setError(null);
    } catch (requestError: unknown) {
      if (!mountedRef.current) {
        return;
      }

      const message =
        requestError instanceof Error
          ? requestError.message
          : "Não foi possível carregar as empresas.";

      setError(message);

      throw requestError;
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    let cancelled = false;

    const loadInitial = async () => {
      try {
        await carregar();
      } catch (requestError) {
        console.error("Falha na carga inicial das empresas:", requestError);
      } finally {
        if (!cancelled && mountedRef.current) {
          setLoading(false);
        }
      }
    };

    void loadInitial();

    const intervalId = window.setInterval(() => {
      if (cancelled || !mountedRef.current) {
        return;
      }

      void carregar().catch((requestError) => {
        console.error("Falha ao atualizar empresas:", requestError);
      });
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;

      mountedRef.current = false;

      window.clearInterval(intervalId);
    };
  }, [carregar]);

  const refresh = useCallback(async () => {
    while (fetchingRef.current && mountedRef.current) {
      await sleep(25);
    }

    if (!mountedRef.current) {
      return;
    }

    await carregar();
  }, [carregar]);

  return {
    data,
    loading,
    error,
    refresh,
    meta,
  };
}
